import { useEffect, useRef, useState } from "react";
import type { Liturgy, Catalog, Plan, PlanItem, AnimateurText, LyricsLib, ValidationStatus } from "./types";
import {
  fetchCatalog, fetchLiturgy, fetchOraisons, generateAnimateur, generateSongsAI,
  fetchPlan, savePlan, fetchMass, saveMass, deleteMass, ConflictError,
} from "./lib/api";
import { getEditor, setEditor as persistEditor } from "./lib/editor";
import { getTeamFilter, setTeamFilter as persistTeamFilter } from "./lib/team";
import { loadCarnet } from "./lib/carnet";
import { norm, type CarnetIndex } from "./lib/songIndex";
import { TEAMS, teamById, teamLabel, DEFAULT_TEAM, type Team } from "./teams";
import { nextSunday, formatFrLong } from "./lib/date";
import { DocState, initDocState, initArchiveDocState, liturgicalNote, validationOf } from "./state";
import { downloadDocx, docxBlob, docxFilename, DOCX_MIME } from "./lib/docx";
import { shareDocxToWhatsApp, shareDocxFile } from "./lib/share";
import { downloadFeuille, feuilleBlob, feuilleFilename } from "./lib/feuilleDocx";
import { Toolbar } from "./components/Toolbar";
import { MassDocument } from "./components/MassDocument";
import { SundayRail, type RailItem, type RailStatus } from "./ui/SundayRail";
import { DayHeader, LiturgicalBadge, Banner, Spinner } from "./ui/display";
import { Button } from "./ui/controls";
import { Icons } from "./ui/icons";
import { useIsMobile } from "./ui/useIsMobile";

type BlockKey = "motAccueil" | "avant1" | "avant2" | "intentions";
const MOMENTS = ["entree", "offertoire", "communion", "envoi"] as const;

function timeFr(iso?: string) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function colorOfSeason(season?: string): string {
  switch (season) {
    case "careme":
    case "avent":
      return "violet";
    case "paques":
    case "noel":
    case "fete":
      return "blanc";
    case "pentecote":
      return "rouge";
    default:
      return "vert";
  }
}

// Validation to show on the rail: prefer the locally-loaded draft (authoritative,
// even while KV's list() metadata is still catching up), else the plan metadata.
function railValidation(local: DocState | undefined, planVal?: ValidationStatus): ValidationStatus | undefined {
  if (local) {
    const s = validationOf(local).status;
    return s === "en_cours" ? undefined : s;
  }
  return planVal;
}

// The rail = our past masses (archives, from the catalog) + the team's curated plan.
function buildRail(catalog: Catalog | null, plan: Plan | null, store: Record<string, DocState>): RailItem[] {
  const map = new Map<string, RailItem>();
  for (const h of catalog?.history || []) {
    map.set(h.date, { date: h.date, dayName: h.day || "Messe", color: colorOfSeason(h.season), status: "archive", team: store[h.date]?.team, viaTool: false, removable: false });
  }
  for (const s of plan?.sundays || []) {
    map.set(s.date, { date: s.date, dayName: s.dayName, color: s.color, status: (s.status || "empty") as RailStatus, validation: railValidation(store[s.date], s.validation), by: s.preparedBy, team: store[s.date]?.team || s.team, viaTool: s.viaTool, removable: true });
  }
  return [...map.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
}

// Compact, team-coloured picker shown on the day card.
function TeamSelect({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const t = teamById(value) || TEAMS[0];
  // Keep a one-off (special) current value selectable even though it's not in the rotation.
  const opts = TEAMS.some((tm) => tm.id === value) ? TEAMS : [...TEAMS, t];
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <span style={{ position: "absolute", left: 11, width: 9, height: 9, borderRadius: "50%", background: t.color, pointerEvents: "none" }} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Équipe responsable de cette messe"
        style={{
          appearance: "none", WebkitAppearance: "none", boxSizing: "border-box",
          fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontWeight: 700,
          color: t.color, background: "var(--bg-raised)",
          border: `1.5px solid ${t.color}`, borderRadius: "var(--radius-pill)",
          padding: "4px 26px 4px 27px", cursor: "pointer", outline: "none",
        }}
      >
        {opts.map((tm) => <option key={tm.id} value={tm.id}>{teamLabel(tm)}</option>)}
      </select>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", right: 10, pointerEvents: "none" }}><path d="m6 9 6 6 6-6" /></svg>
    </span>
  );
}

// Read-only team chip for past masses (same look as the picker, nothing to change).
function TeamChip({ team: t }: { team: Team }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, boxSizing: "border-box", fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontWeight: 700, color: t.color, background: "var(--bg-raised)", border: `1.5px solid ${t.color}`, borderRadius: "var(--radius-pill)", padding: "4px 12px 4px 11px" }}>
      <span style={{ width: 9, height: 9, borderRadius: "50%", background: t.color, flexShrink: 0 }} />
      {teamLabel(t)}
    </span>
  );
}

// Calendar filter: "Toutes les équipes" (no filter) or one team.
function TeamFilter({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const t = teamById(value);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      {t && <span style={{ position: "absolute", left: 11, width: 8, height: 8, borderRadius: "50%", background: t.color, pointerEvents: "none" }} />}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filtrer le calendrier par équipe"
        style={{
          appearance: "none", WebkitAppearance: "none", boxSizing: "border-box",
          fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontWeight: 600,
          color: t ? t.color : "var(--text-secondary)", background: "var(--bg-raised)",
          border: `1px solid ${t ? t.color : "var(--border-strong)"}`, borderRadius: "var(--radius-pill)",
          padding: t ? "3px 24px 3px 25px" : "3px 24px 3px 11px", cursor: "pointer", outline: "none",
        }}
      >
        <option value="">Toutes les équipes</option>
        {TEAMS.map((tm) => <option key={tm.id} value={tm.id}>Équipe {tm.name}</option>)}
      </select>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={t ? t.color : "var(--text-muted)"} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", right: 9, pointerEvents: "none" }}><path d="m6 9 6 6 6-6" /></svg>
    </span>
  );
}

export default function App() {
  const isMobile = useIsMobile();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [store, setStore] = useState<Record<string, DocState>>({});
  const [baseVersions, setBaseVersions] = useState<Record<string, number>>({});
  const [editor, setEditorState] = useState(getEditor());
  const [teamFilter, setTeamFilterState] = useState(getTeamFilter());
  // Opt-in per session, off by default (a deliberate choice each time, not sticky).
  const [carnetOnly, setCarnetOnly] = useState(false);

  const [loading, setLoading] = useState(false);
  const [aiAll, setAiAll] = useState(false);
  const [songAiAll, setSongAiAll] = useState(false);
  const [aiBlock, setAiBlock] = useState<BlockKey | null>(null);
  const [songAiMoment, setSongAiMoment] = useState<string | null>(null);
  const [refrainBusy, setRefrainBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ date: string; current: any } | null>(null);
  const [shareHint, setShareHint] = useState(false); // shown when WhatsApp share fell back to download

  // Refs so the debounced/async save path always reads the latest values.
  const storeRef = useRef(store); storeRef.current = store;
  const baseRef = useRef(baseVersions); baseRef.current = baseVersions;
  const editorRef = useRef(editor); editorRef.current = editor;
  const dateRef = useRef(date); dateRef.current = date;
  const dirtyRef = useRef(false);
  const saveTimer = useRef<number | undefined>(undefined);
  const lyricsLibRef = useRef<LyricsLib | null>(null);
  const carnetRef = useRef<CarnetIndex | null>(null);
  const didAutoSelect = useRef(false);
  // A ready-to-share .docx File, rebuilt off-gesture so navigator.share() can run
  // synchronously inside the click (iOS Safari drops the share otherwise).
  const shareFileRef = useRef<File | null>(null);
  const shareArmedDateRef = useRef<string | null>(null); // which mass shareFileRef holds

  const doc = date ? store[date] : undefined;
  const planItem = plan?.sundays.find((s) => s.date === date);
  const historyRec = catalog?.history.find((h) => h.date === date);
  const isArchive = planItem?.status === "archive" || (!!historyRec && !planItem);
  // A past mass has real sheets to look back at if it was prepared in the app, or if
  // it's one of the pre-app masses rebuilt from its original document.
  const archiveViaTool = !!planItem?.viaTool;
  const archiveHasSheets = archiveViaTool || !!historyRec;
  // Past masses show their recorded team only (pre-app archives have none).
  const archiveTeam = isArchive ? teamById(doc?.team || planItem?.team) : undefined;

  // Never let a stale read (KV is eventually consistent) revert a newer local plan.
  function refreshPlan() {
    fetchPlan()
      .then((p) => setPlan((cur) => (cur && p.version < cur.version ? cur : p)))
      .catch(() => {});
  }

  // ---- initial load ----
  useEffect(() => {
    fetchCatalog().then(setCatalog).catch((e) => setError(`Catalogue : ${e.message}`));
    loadCarnet().then((m) => { carnetRef.current = m; });
    refreshPlan();
  }, []);

  // Pick the Sunday ONCE on first load — never again, so a plan refetch (poll /
  // post-save) can't steal the user's current selection. A shared `?date=` link
  // (deep link) wins over the default; otherwise pick the first upcoming Sunday.
  useEffect(() => {
    if (didAutoSelect.current || !catalog || !plan || date !== null) return;
    const urlDate = new URLSearchParams(window.location.search).get("date");
    if (urlDate && /^\d{4}-\d{2}-\d{2}$/.test(urlDate)) {
      didAutoSelect.current = true;
      void select(urlDate);
      return;
    }
    const items = buildRail(catalog, plan, storeRef.current);
    const pick = items.length ? items.find((r) => r.status !== "archive") || items[items.length - 1] : null;
    if (pick) {
      didAutoSelect.current = true;
      void select(pick.date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, plan]);

  // Keep the URL in sync with the selected Sunday so the link is shareable.
  useEffect(() => {
    if (!date) return;
    const want = `?date=${date}`;
    if (window.location.search !== want) window.history.replaceState(null, "", window.location.pathname + want);
  }, [date]);

  // ---- freshness: refetch plan on focus + every 30s; pull others' edits when not dirty ----
  useEffect(() => {
    let lastFocusRefresh = 0;
    const onFocus = () => {
      // Throttle: rapid blur/focus cycles shouldn't hammer the plan endpoint.
      if (Date.now() - lastFocusRefresh < 5000) return;
      lastFocusRefresh = Date.now();
      refreshPlan();
      const d = dateRef.current;
      if (d && !dirtyRef.current && !saveTimer.current) {
        fetchMass(d).then((stored) => {
          if (stored && stored.version > (baseRef.current[d] ?? 0)) {
            setStore((s) => ({ ...s, [d]: stored.doc as DocState }));
            setBaseVersions((v) => ({ ...v, [d]: stored.version }));
          }
        }).catch(() => {});
      }
    };
    window.addEventListener("focus", onFocus);
    const id = window.setInterval(refreshPlan, 30000);
    const onBefore = () => {
      const d = dateRef.current;
      if (d && dirtyRef.current && storeRef.current[d]) {
        const payload = JSON.stringify({ baseVersion: baseRef.current[d] ?? 0, editor: editorRef.current, doc: storeRef.current[d] });
        navigator.sendBeacon?.(`/api/mass?date=${d}`, new Blob([payload], { type: "application/json" }));
      }
    };
    window.addEventListener("beforeunload", onBefore);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(id);
      window.removeEventListener("beforeunload", onBefore);
    };
  }, []);

  // Keep the feuille animateur .docx armed as a File so the WhatsApp button can
  // call navigator.share() with NO preceding await (iOS drops the share otherwise).
  // Build it EAGERLY when a mass opens (so a tap always has the document to share,
  // never the text-only fallback), and refresh it debounced as the doc is edited.
  useEffect(() => {
    if (!doc || !date) { shareFileRef.current = null; shareArmedDateRef.current = null; return; }
    let cancelled = false;
    const build = async () => {
      try {
        const blob = await docxBlob(doc);
        if (!cancelled) { shareFileRef.current = new File([blob], docxFilename(doc), { type: DOCX_MIME }); shareArmedDateRef.current = date; }
      } catch { /* ignore — fallback path downloads on demand */ }
    };
    if (shareArmedDateRef.current !== date) {
      // Switched to a different mass: drop the old file so it can't be shared, build now.
      shareFileRef.current = null;
      void build();
      return () => { cancelled = true; };
    }
    const t = window.setTimeout(build, 400); // same mass, content edit → refresh
    return () => { cancelled = true; window.clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, date]);

  // ---- saving ----
  // Returns true on a successful save, false on conflict/error (so callers like
  // setValidation know whether to refresh the rail).
  async function doSave(d: string): Promise<boolean> {
    const ds = storeRef.current[d];
    if (!ds) return false;
    setSaveState("saving");
    try {
      const stored = await saveMass(d, baseRef.current[d] ?? 0, editorRef.current, ds);
      setBaseVersions((v) => ({ ...v, [d]: stored.version }));
      dirtyRef.current = false;
      setSaveState("saved");
      setSavedAt(stored.updatedAt);
      if (stored.version === 1) refreshPlan(); // first save → rail flips to "draft"
      return true;
    } catch (e: any) {
      if (e instanceof ConflictError) setConflict({ date: d, current: e.current });
      else setError(`Enregistrement : ${e.message}`);
      setSaveState("idle");
      return false;
    }
  }
  function scheduleSave() {
    const d = dateRef.current;
    if (!d) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => { saveTimer.current = undefined; void doSave(d); }, 1500);
  }
  async function flushSave(): Promise<boolean> {
    if (saveTimer.current) { window.clearTimeout(saveTimer.current); saveTimer.current = undefined; }
    const d = dateRef.current;
    if (d && dirtyRef.current && storeRef.current[d]) return await doSave(d);
    return true; // nothing pending counts as success
  }

  function patch(p: Partial<DocState>) {
    const d = dateRef.current;
    if (!d) return;
    setStore((s) => {
      const cur = s[d];
      if (!cur) return s;
      return { ...s, [d]: { ...cur, ...p } };
    });
    dirtyRef.current = true;
    scheduleSave();
  }

  // ---- selection / plan management ----
  async function select(d: string) {
    if (!catalog) return;
    await flushSave();
    setDate(d);
    setError(null);
    setConflict(null);
    setShareHint(false);
    if (storeRef.current[d]) return;
    setLoading(true);
    try {
      const stored = await fetchMass(d);
      if (stored) {
        setStore((s) => ({ ...s, [d]: stored.doc as DocState }));
        setBaseVersions((v) => ({ ...v, [d]: stored.version }));
      } else {
        const [liturgy, oraisons] = await Promise.all([fetchLiturgy(d), fetchOraisons(d)]);
        const full = { ...liturgy, oraisons };
        const rec = catalog.history.find((h) => h.date === d);
        const fresh = rec ? initArchiveDocState(rec, full, catalog) : initDocState(full, catalog);
        if (!rec) fresh.team = plan?.sundays.find((s) => s.date === d)?.team || DEFAULT_TEAM; // inherit the scheduled team
        setStore((s) => ({ ...s, [d]: fresh }));
        setBaseVersions((v) => ({ ...v, [d]: 0 }));
      }
      dirtyRef.current = false;
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function addSunday(d: string) {
    if (!catalog || !plan) return;
    if (plan.sundays.some((s) => s.date === d)) { void select(d); return; }
    await flushSave();
    setLoading(true);
    setError(null);
    try {
      let liturgy: Liturgy;
      const stored = await fetchMass(d);
      if (stored) {
        setStore((s) => ({ ...s, [d]: stored.doc as DocState }));
        setBaseVersions((v) => ({ ...v, [d]: stored.version }));
        liturgy = (stored.doc as DocState).liturgy;
      } else {
        const [lit, oraisons] = await Promise.all([fetchLiturgy(d), fetchOraisons(d)]);
        liturgy = { ...lit, oraisons };
        const rec = catalog.history.find((h) => h.date === d);
        const fresh = rec ? initArchiveDocState(rec, liturgy, catalog) : initDocState(liturgy, catalog);
        if (!rec) fresh.team = plan?.sundays.find((s) => s.date === d)?.team || DEFAULT_TEAM; // inherit the scheduled team
        setStore((s) => ({ ...s, [d]: fresh }));
        setBaseVersions((v) => ({ ...v, [d]: 0 }));
      }
      const item: PlanItem = { date: d, dayName: liturgy.dayName, color: liturgy.color || "vert", year: liturgy.year, seasonKey: liturgy.seasonKey };
      const result = await savePlan(plan.version, editorRef.current, [...plan.sundays, item]);
      setPlan(result);
      setDate(d);
      dirtyRef.current = false;
    } catch (e: any) {
      if (e instanceof ConflictError) { setPlan(e.current); setError("La liste a changé ailleurs — réessayez d'ajouter ce dimanche."); }
      else setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function removeSunday(d: string) {
    if (!plan) return;
    if (!window.confirm("Retirer ce dimanche de la liste ? Le brouillon enregistré sera supprimé.")) return;
    try {
      const result = await savePlan(plan.version, editorRef.current, plan.sundays.filter((s) => s.date !== d));
      await deleteMass(d);
      setPlan(result);
      setStore((s) => { const n = { ...s }; delete n[d]; return n; });
      if (dateRef.current === d) {
        const next = result.sundays.find((s) => s.status !== "archive")?.date || result.sundays[0]?.date || null;
        if (next) void select(next);
        else setDate(null);
      }
    } catch (e: any) {
      if (e instanceof ConflictError) { setPlan(e.current); setError("La liste a changé ailleurs."); }
      else setError(e.message);
    }
  }

  // ---- conflict resolution ----
  function reloadConflict() {
    if (!conflict) return;
    const { date: d, current } = conflict;
    setStore((s) => ({ ...s, [d]: current.doc as DocState }));
    setBaseVersions((v) => ({ ...v, [d]: current.version }));
    dirtyRef.current = false;
    setConflict(null);
    setSaveState("saved");
    setSavedAt(current.updatedAt);
  }
  async function keepMine() {
    if (!conflict) return;
    const { date: d, current } = conflict;
    setConflict(null);
    setSaveState("saving");
    try {
      const stored = await saveMass(d, current.version, editorRef.current, storeRef.current[d]);
      setBaseVersions((v) => ({ ...v, [d]: stored.version }));
      dirtyRef.current = false;
      setSaveState("saved");
      setSavedAt(stored.updatedAt);
      refreshPlan(); // our validation/content may differ from the rail's stale metadata
    } catch (e: any) {
      if (e instanceof ConflictError) setConflict({ date: d, current: e.current });
      else setError(e.message);
      setSaveState("idle");
    }
  }

  // ---- AI ----
  function applyAnimateur(t: AnimateurText, only?: BlockKey) {
    const intentions = t.intentions?.length ? t.intentions.join("\n") : "";
    if (only === "intentions") { if (intentions) patch({ prierePuIntentions: intentions }); return; }
    const map = { motAccueil: t.motAccueil, avant1: t.avant1, avant2: t.avant2 };
    if (only) { patch({ [only]: (map as any)[only] } as Partial<DocState>); return; }
    patch({ motAccueil: t.motAccueil, avant1: t.avant1, avant2: t.avant2, ...(intentions ? { prierePuIntentions: intentions } : {}) });
  }
  // Keep the admin-facing "clé non configurée" message, but soften runtime
  // failures for the non-technical team.
  const aiErrorMessage = (e: any): string =>
    /configur/i.test(e?.message || "")
      ? e.message
      : "La préparation automatique est momentanément indisponible. Réessayez dans quelques minutes, ou complétez à la main.";

  async function generateAll() {
    if (!doc) return;
    setAiAll(true); setAiError(null);
    try { applyAnimateur(await generateAnimateur(doc.liturgy)); }
    catch (e: any) { setAiError(aiErrorMessage(e)); }
    finally { setAiAll(false); }
  }
  async function regenerate(block: BlockKey) {
    if (!doc) return;
    setAiBlock(block); setAiError(null);
    try { applyAnimateur(await generateAnimateur(doc.liturgy, [block]), block); }
    catch (e: any) { setAiError(aiErrorMessage(e)); }
    finally { setAiBlock(null); }
  }
  async function aiSongs(moments?: string[]) {
    if (!doc || !catalog) return;
    const single = moments && moments.length === 1 ? moments[0] : null;
    if (single) setSongAiMoment(single); else setSongAiAll(true);
    setAiError(null);
    try {
      const carnet = carnetRef.current;
      const useCarnet = carnetOnly && !!carnet && carnet.size > 0;
      // Carnet-only: let the generator choose strictly from the songbook, enriched
      // with the catalog's moment/season data + the thematic category, where known.
      const repertoire = useCarnet
        ? [...carnet!.values()].map((c) => {
            const s = catalog.songs.find((x) => norm(x.title) === norm(c.title));
            return { title: c.title, moments: s?.moments || [], seasons: s?.seasons || [], usedCount: s?.usedCount || 0, category: c.cat };
          })
        : catalog.songs.map((s) => ({ title: s.title, moments: s.moments, seasons: s.seasons, usedCount: s.usedCount }));
      const res = await generateSongsAI(doc.liturgy, repertoire, moments, useCarnet);
      const updates: Partial<DocState> = {};
      for (const m of MOMENTS) {
        const rec = (res as any)[m];
        if (!rec || !rec.title) continue;
        const cat = catalog.songs.find((s) => s.title.toLowerCase() === String(rec.title).toLowerCase());
        const cur = doc[m];
        const reason = rec.reason ? String(rec.reason) : undefined;
        // If the chosen song is in the carnet, fill its page (annotation "carnet");
        // otherwise keep the existing/catalog annotation.
        const hit = carnet?.get(norm(String(rec.title)));
        (updates as any)[m] = hit
          ? { title: hit.title, annotation: "carnet", page: hit.page, reason }
          : { title: rec.title, annotation: cur.annotation || cat?.annotations?.[0] || (m === "offertoire" ? "chorale seule" : "feuille"), page: "", reason };
      }
      if (Object.keys(updates).length) patch(updates);
    } catch (e: any) {
      setAiError(aiErrorMessage(e));
    } finally {
      if (single) setSongAiMoment(null); else setSongAiAll(false);
    }
  }

  // Fill the psalm refrain from the official AELF antienne. The draft generator
  // calls it only when empty (won't clobber an edit); the field's refill button
  // forces it. The stored draft's frozen liturgy may predate refrain parsing, so we
  // fall back to (or, when forced, prefer) a fresh AELF fetch.
  async function fillPsalmRefrain(force = false) {
    const d = dateRef.current;
    if (!d) return;
    const ds = storeRef.current[d];
    if (!ds || !ds.liturgy.psalm) return; // no psalm that day → nothing to fill
    // psalmRefrain may be undefined on drafts created before the field existed —
    // access it null-safely (calling .trim() on undefined here is what silently
    // broke the draft generator while the forced refill button still worked).
    if (!force && (ds.psalmRefrain || "").trim()) return; // already filled, don't overwrite
    setRefrainBusy(true);
    try {
      let refrain = ds.liturgy.psalm.refrain?.trim() || "";
      if (force || !refrain) {
        try { const fresh = await fetchLiturgy(d); refrain = fresh.psalm?.refrain?.trim() || refrain; } catch { /* keep frozen */ }
      }
      if (refrain && (force || !(storeRef.current[d]?.psalmRefrain || "").trim())) patch({ psalmRefrain: refrain });
    } finally {
      setRefrainBusy(false);
    }
  }

  function prepareAll() {
    const d = dateRef.current;
    const ds = d ? storeRef.current[d] : null;
    // Don't silently overwrite text someone already wrote/generated.
    const hasContent = !!(ds && ((ds.motAccueil || "").trim() || (ds.avant1 || "").trim() || (ds.avant2 || "").trim()));
    if (hasContent && !window.confirm("Cela va remplacer le mot d'accueil, les phrases d'introduction et les chants déjà proposés. Continuer ?")) return;
    void generateAll(); void aiSongs(); void fillPsalmRefrain(false);
  }

  function download() {
    if (!doc) return;
    // On phones, open the OS share sheet (send/save easily) instead of a hard-to-
    // find download; the animateur file is pre-armed so iOS can share in-gesture.
    if (isMobile) {
      const armed = shareFileRef.current;
      if (armed) { void shareDocxFile(armed, "Feuille animateur"); return; }
      void docxBlob(doc).then((b) => shareDocxFile(new File([b], docxFilename(doc!), { type: b.type }), "Feuille animateur"));
      return;
    }
    void downloadDocx(doc);
  }

  async function downloadFeuilleDoc() {
    if (!doc) return;
    if (!lyricsLibRef.current) {
      lyricsLibRef.current = await fetch("/data/lyrics.json")
        .then((r) => r.json())
        .catch(() => ({ songs: {}, ordinaries: {} }) as LyricsLib);
    }
    if (isMobile) {
      const blob = await feuilleBlob(doc, lyricsLibRef.current!);
      void shareDocxFile(new File([blob], feuilleFilename(doc), { type: blob.type }), "Feuille assemblée");
      return;
    }
    void downloadFeuille(doc, lyricsLibRef.current!);
  }

  function shareMessage(d: DocState): string {
    return `Bonjour mon Père, voici la feuille d'animation pour la messe du ${formatFrLong(d.date)} (${d.liturgy.dayName}), prête pour votre validation. Merci !`;
  }
  // Must stay synchronous up to navigator.share() (no await) for iOS Safari.
  function shareToPriest() {
    const d = dateRef.current;
    if (!d) return;
    const ds = storeRef.current[d];
    if (!ds) return;
    const text = shareMessage(ds);
    const file = shareFileRef.current;
    if (file) {
      void shareDocxToWhatsApp(file, text).then((r) => { if (r === "downloaded") setShareHint(true); });
    } else {
      // Ref not armed yet (just opened) — fall back to a download + WhatsApp text.
      void downloadDocx(ds);
      try { window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank", "noopener"); } catch { /* ignore */ }
      setShareHint(true);
    }
    void flushSave(); // persist the version we just shared (runs after share is dispatched)
  }

  function changeEditor(v: string) { setEditorState(v); persistEditor(v); }
  // The team for this mass is shared with everyone via the normal autosave (which
  // reads the fresh store, unlike an immediate flush). It reaches the DB + the rail
  // metadata, so other users see the same value on their next refresh.
  function changeTeam(id: string) { patch({ team: id }); }
  function changeTeamFilter(id: string) { setTeamFilterState(id); persistTeamFilter(id); }

  const allRailItems = buildRail(catalog, plan, store);
  // Local-only filter: show only the chosen team's masses. Archives match only on a
  // recorded team (never the default), so pre-tool archives hide. Empty = everything.
  const railItems = teamFilter ? allRailItems.filter((it) => (it.status === "archive" ? it.team : it.team || DEFAULT_TEAM) === teamFilter) : allRailItems;

  const saveLabel = saveState === "saving" ? "Enregistrement…" : saveState === "saved" ? `Enregistré${savedAt ? ` · ${timeFr(savedAt)}` : ""}` : "";
  const l = doc?.liturgy;

  return (
    <div style={{ width: "min(820px, 94vw)", margin: "0 auto", paddingTop: "4px" }}>
      <Toolbar
        date={date || nextSunday()}
        onDate={addSunday}
        editor={editor}
        onEditor={changeEditor}
      />

      {/* Desktop-only nudge toward the date picker (top-right of the toolbar). */}
      {!isMobile && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start", gap: 6, marginTop: -12, marginBottom: 16, paddingRight: 4 }}>
          <span style={{ marginTop: 6, maxWidth: 300, textAlign: "right", fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontStyle: "italic", color: "var(--text-muted)", lineHeight: 1.4 }}>
            Ajouter une messe facilement en choisissant n'importe quelle date
          </span>
          <svg width="40" height="34" viewBox="0 0 40 34" fill="none" aria-hidden style={{ flexShrink: 0, color: "var(--accent)" }}>
            <path d="M4 30 C 16 28, 28 22, 33 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
            <path d="M26 8 L 33 6 L 31 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </div>
      )}

      {allRailItems.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--text-sm)", letterSpacing: "var(--tracking-eyebrow)", textTransform: "uppercase", color: "var(--text-muted)" }}>Le calendrier</span>
            <TeamFilter value={teamFilter} onChange={changeTeamFilter} />
          </div>
          {railItems.length > 0 ? (
            <SundayRail items={railItems} value={date || ""} onSelect={select} onRemove={removeSunday} />
          ) : (
            <div style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-sm)", color: "var(--text-muted)", padding: "12px 2px" }}>
              Aucune messe pour cette équipe.{" "}
              <button type="button" onClick={() => changeTeamFilter("")} style={{ border: "none", background: "transparent", color: "var(--accent-text)", fontFamily: "var(--font-ui)", fontSize: "var(--text-sm)", fontWeight: 600, cursor: "pointer", padding: 0 }}>Voir toutes les équipes</button>
            </div>
          )}
        </div>
      )}

      {error && <div style={{ marginBottom: 14 }}><Banner tone="danger">{error}</Banner></div>}
      {aiError && <div style={{ marginBottom: 14 }}><Banner tone="danger">{aiError}</Banner></div>}
      {conflict && (
        <div style={{ marginBottom: 14 }}>
          <Banner tone="warning" action={
            <span style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <Button size="sm" variant="secondary" onClick={reloadConflict}>Recharger</Button>
              <Button size="sm" variant="sacred" onClick={keepMine}>Garder mes modifications</Button>
            </span>
          }>
            {conflict.current?.updatedBy ? `${conflict.current.updatedBy} a` : "Quelqu'un a"} modifié ce document entre-temps. Rechargez sa version, ou gardez la vôtre (écrase la sienne).
          </Banner>
        </div>
      )}

      {/* Nothing in the rail at all → prompt to add the first Sunday */}
      {plan && allRailItems.length === 0 ? (
        <div style={{ textAlign: "center", padding: "64px 16px", color: "var(--text-muted)" }}>
          <p style={{ fontFamily: "var(--font-serif)", fontSize: "var(--text-lg)", color: "var(--text-primary)", margin: "0 0 6px" }}>Aucun dimanche dans la liste</p>
          <p style={{ margin: "0 0 18px" }}>Ajoutez le dimanche dont votre équipe est responsable. Il sera partagé avec tout le groupe.</p>
          <Button variant="primary" onClick={() => addSunday(nextSunday())} disabled={loading || !catalog}
            style={isMobile ? { width: "100%", whiteSpace: "normal", maxWidth: "100%" } : undefined}>
            {loading ? "Ajout…"
              : isMobile ? "Ajouter le prochain dimanche"
              : `Ajouter le prochain dimanche (${formatFrLong(nextSunday())})`}
          </Button>
          <p style={{ marginTop: 12, fontSize: "var(--text-sm)", color: "var(--text-faint)" }}>… ou choisissez une autre date via le calendrier en haut.</p>
        </div>
      ) : !plan ? (
        <div style={{ textAlign: "center", padding: "72px 0" }}><Spinner label="Chargement…" /></div>
      ) : (
        <div style={{ width: "min(760px, 100%)", margin: "0 auto" }}>
          {loading || !doc || !l ? (
            <div style={{ textAlign: "center", padding: "72px 0" }}><Spinner label="Ouverture du dimanche…" /></div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <DayHeader dayName={l.dayName} date={formatFrLong(date!)} year={l.year} color={l.color || "vert"} badge={<LiturgicalBadge color={l.color || "vert"} />} team={isArchive ? archiveTeam && <TeamChip team={archiveTeam} /> : <TeamSelect value={doc.team || planItem?.team || DEFAULT_TEAM} onChange={changeTeam} />} />

              {!isArchive && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <div style={{ width: "100%", maxWidth: 460, display: "flex", flexDirection: "column" }}>
                    <Button variant="sacred" iconLeft={<Icons.Sparkle size={16} />} onClick={prepareAll} disabled={aiAll || songAiAll} style={{ width: "100%", justifyContent: "center", whiteSpace: "normal", lineHeight: 1.3, textAlign: "center", borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
                      {aiAll || songAiAll ? "Préparation…" : "Générer un brouillon automatiquement (c'est magique)"}
                    </Button>
                    {/* Carnet-only is an option OF the generate action — attached under it, off by default. */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={carnetOnly}
                      onClick={() => setCarnetOnly((v) => !v)}
                      title="Limiter les chants proposés à ceux du carnet de chant de la paroisse"
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                        width: "100%", boxSizing: "border-box", padding: "7px 12px", cursor: "pointer",
                        border: "1px solid var(--border-strong)", borderTop: "none",
                        borderBottomLeftRadius: "var(--radius-md)", borderBottomRightRadius: "var(--radius-md)",
                        background: carnetOnly ? "#fbf1de" : "var(--bg-raised)",
                        color: carnetOnly ? "#8a5a1a" : "var(--text-muted)",
                        fontFamily: "var(--font-ui)", fontSize: "var(--text-sm)", fontWeight: 600,
                        transition: "background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)",
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
                      Seulement les chants du carnet
                      <span aria-hidden style={{ position: "relative", width: 30, height: 17, borderRadius: 9, flexShrink: 0, background: carnetOnly ? "#8a5a1a" : "var(--border-strong)", transition: "background var(--dur-fast) var(--ease-out)" }}>
                        <span style={{ position: "absolute", top: 2, left: carnetOnly ? 15 : 2, width: 13, height: 13, borderRadius: "50%", background: "#fff", transition: "left var(--dur-fast) var(--ease-out)" }} />
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {liturgicalNote(l.seasonKey) && <Banner tone="warning">⛪ {liturgicalNote(l.seasonKey)}</Banner>}

              {isArchive ? (
                <>
                  <Banner tone={archiveViaTool ? "info" : "neutral"}>
                    {archiveViaTool ? (
                      <>Messe passée, <strong>préparée avec l'outil</strong>{planItem?.preparedBy ? ` (dernière modification : ${planItem.preparedBy})` : ""}. Téléchargez les feuilles ci-dessous (consultation seule).</>
                    ) : historyRec ? (
                      <>Messe passée, <strong>préparée sans l'outil</strong> : feuilles reconstituées à partir du document d'origine (consultation seule).</>
                    ) : (
                      <>Messe passée, <strong>préparée sans l'outil</strong> : aucune feuille n'a été enregistrée ici.</>
                    )}
                  </Banner>
                  {archiveHasSheets && (
                    <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", padding: "32px 0 12px" }}>
                      <Button variant="secondary" iconLeft={<Icons.Music size={16} />} onClick={downloadFeuilleDoc}>
                        Feuille assemblée
                      </Button>
                      <Button variant="primary" iconLeft={<Icons.Download size={16} />} onClick={download}>
                        Feuille animateur
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {planItem?.status === "draft" ? (
                    <Banner tone="info">Brouillon partagé{planItem.preparedBy ? ` — préparé par ${planItem.preparedBy}` : ""}. Vos modifications sont enregistrées automatiquement pour toute l'équipe.</Banner>
                  ) : (
                    <Banner tone="warning">Brouillon vierge. Cliquez « Générer un brouillon automatiquement » ou rédigez ; tout est enregistré et partagé.</Banner>
                  )}

                  {!editor.trim() && (
                    <Banner tone="warning">Indiquez votre prénom en haut de la page : il signe vos modifications auprès de l'équipe.</Banner>
                  )}

                  {shareHint && (
                    <Banner tone="info" action={<Button size="sm" variant="ghost" onClick={() => setShareHint(false)}>OK</Button>}>
                      Feuille téléchargée — joignez-la dans WhatsApp au message destiné au Père.
                    </Banner>
                  )}

                  <MassDocument
                    doc={doc}
                    catalog={catalog!}
                    onPatch={patch}
                    onRegen={regenerate}
                    aiBlock={aiBlock}
                    onDownload={download}
                    onDownloadFeuille={downloadFeuilleDoc}
                    onShare={shareToPriest}
                    saveLabel={saveLabel}
                    onRegenSong={(m) => aiSongs([m])}
                    songAiMoment={songAiMoment}
                    onRefillRefrain={() => fillPsalmRefrain(true)}
                    refrainBusy={refrainBusy}
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
